import { useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from './cx';
import { IconArrowDown, IconArrowUp, IconSort } from './icons';
import { Skeleton } from './Surface';
import { useKit } from './strings';

export type TableColumn<R> = {
  key: string;
  header: string;
  cell?: (row: R) => ReactNode;
  /** Value used for sorting; when absent the column is not sortable. */
  sortValue?: (row: R) => string | number;
  align?: 'start' | 'end';
  /** Label for the stacked phone layout; defaults to header. Pass '' to hide it (e.g. an actions column). */
  mobileLabel?: string;
};
export type SortState = { key: string; dir: 'asc' | 'desc' } | null;

/** Stable sort of rows by a column's sortValue (locale-aware for strings, so Arabic sorts correctly). */
export function sortRows<R>(rows: R[], columns: TableColumn<R>[], sort: SortState, locale = 'en'): R[] {
  if (!sort) return rows;
  const col = columns.find((c) => c.key === sort.key);
  if (!col?.sortValue) return rows;
  const coll = new Intl.Collator(locale, { numeric: true, sensitivity: 'base' });
  const sign = sort.dir === 'asc' ? 1 : -1;
  return rows
    .map((r, i) => ({ r, i, v: col.sortValue!(r) }))
    .sort((a, b) => {
      const d = typeof a.v === 'number' && typeof b.v === 'number' ? a.v - b.v : coll.compare(String(a.v), String(b.v));
      return d * sign || a.i - b.i;
    })
    .map((x) => x.r);
}

/** asc -> desc -> unsorted. */
export const nextSort = (cur: SortState, key: string): SortState =>
  !cur || cur.key !== key ? { key, dir: 'asc' } : cur.dir === 'asc' ? { key, dir: 'desc' } : null;

/**
 * Data table: caption for screen readers, scoped headers, sortable columns (aria-sort), keyboard-activatable rows,
 * skeleton rows while loading, an empty slot, and a stacked card layout under 640 px (stack, default on).
 */
export function Table<R extends { id: string }>({ caption, columns, rows, loading, empty, onRowClick, rowLabel, defaultSort = null, stack = true, className }: {
  caption: string;
  columns: TableColumn<R>[];
  rows: R[];
  loading?: boolean;
  empty?: ReactNode;
  onRowClick?: (row: R) => void;
  /** Accessible name for a clickable row (e.g. "Open lead Rana"). */
  rowLabel?: (row: R) => string;
  defaultSort?: SortState;
  stack?: boolean;
  className?: string;
}) {
  const { locale, strings } = useKit();
  const [sort, setSort] = useState<SortState>(defaultSort);
  const sorted = useMemo(() => sortRows(rows, columns, sort, locale), [rows, columns, sort, locale]);
  const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>, r: R) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick?.(r); }
  };
  return (
    <div className={cx('qk-table-wrap', className)} data-stack={stack || undefined}>
      <table className="qk-table">
        <caption className="qk-sr-only">
          {caption}
          {sort ? `, ${columns.find((c) => c.key === sort.key)?.header ?? ''} ${sort.dir === 'asc' ? strings.sortAscending : strings.sortDescending}` : ''}
        </caption>
        <thead>
          <tr>
            {columns.map((c) => {
              const active = sort?.key === c.key;
              return (
                <th key={c.key} scope="col" data-align={c.align} aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                  {c.sortValue ? (
                    <button type="button" className="qk-table__sort" onClick={() => setSort((s) => nextSort(s, c.key))}>
                      {c.header}
                      {active ? (sort!.dir === 'asc' ? <IconArrowUp width={14} height={14} /> : <IconArrowDown width={14} height={14} />) : <IconSort width={14} height={14} />}
                    </button>
                  ) : c.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading
            ? Array.from({ length: 4 }, (_, i) => (
                <tr key={`sk${i}`} aria-hidden="true">
                  {columns.map((c) => <td key={c.key} data-label=""><Skeleton width={c.align === 'end' ? '3rem' : '70%'} /></td>)}
                </tr>
              ))
            : sorted.length === 0
              ? (
                <tr>
                  <td colSpan={columns.length} data-label="" className="qk-table__empty">{empty}</td>
                </tr>
              )
              : sorted.map((r) => (
                <tr
                  key={r.id}
                  data-clickable={onRowClick ? '' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  aria-label={onRowClick && rowLabel ? rowLabel(r) : undefined}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  onKeyDown={onRowClick ? (e) => onRowKey(e, r) : undefined}
                >
                  {columns.map((c) => (
                    <td key={c.key} data-align={c.align} data-label={c.mobileLabel ?? c.header}>
                      {c.cell ? c.cell(r) : String((r as Record<string, unknown>)[c.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
      {loading ? <span className="qk-sr-only" role="status">{strings.loading}</span> : null}
    </div>
  );
}
